import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { INSPECTION_POOL_READ, QUALITY_PROGRESS_READ, type Whoami } from '../auth'
import { StartPanel } from '../carta/StartPanel'
import { Hint } from '../carta/Hint'
import { ModuleRail } from '../carta/ModuleRail'
import { StatusPill } from '../carta/StatusPill'
import { useT } from '../i18n/LocaleProvider'
import { defaultPathFor } from '../home'
import { commesse } from '../inspections'
import { readProgress, type QualityProgress } from '../quality'
import { countPendingIntents } from '../reviewStore'
import { inspectionFromSearch } from '../routes/InspectionLayout'
import { AiutoTabs, isAiutoScreen } from '../aiuto/AiutoTabs'
import { ScreenPurpose } from '../carta/ScreenPurpose'
import { AdminTabs, isAdminScreen } from '../workspace/AdminTabs'
import { WorkspaceHeader } from '../workspace/WorkspaceHeader'
import { isTabScreen, WorkspaceTabs } from '../workspace/WorkspaceTabs'
import type { Commessa } from '../types'

export interface CartaShellProps {
  title: string
  who: Whoami
  onLeave: () => void
  children: ReactNode
  /**
   * Il nome dell'arrivo, quando l'indirizzo cambia senza cambiare schermata.
   *
   * Una schermata a filtri riscrive l'indirizzo a ogni casella spuntata: quello
   * non e' un arrivo, e portare il fuoco sul titolo toglierebbe la tastiera di
   * mano proprio a chi sta filtrando — un `Tab` per tornare al controllo, a ogni
   * spunta. Con un nome fisso l'arrivo e' uno.
   */
  announce?: string
  /**
   * Una riga sotto il titolo, quando il titolo dice che cosa si vede ma non
   * come funziona. Il pool ne ha bisogno: si chiama «Commesse» perche' e' un
   * elenco di commesse, ma e' condiviso, e chi non lo sa non capisce perche'
   * una riga risulta gia' in lavorazione da qualcun altro.
   */
  subtitle?: string
  /** Job ingest della commessa, quando la pagina lo conosce gia'. */
  jobId?: string | null
  /**
   * Il contorno della barra. La Carta e' chiara, ma sopra il banco la barra
   * e' scura come il banco (ADR-009): una fascia bianca sopra un fotogramma
   * che si guarda per ore altera il giudizio sul contrasto, ed e' sul
   * contrasto che si decide. Lo dice la route, perche' e' la route a sapere
   * che cosa monta.
   */
  contour?: 'carta' | 'cockpit'
}

export function CartaShell({
  title,
  who,
  onLeave,
  children,
  announce,
  subtitle,
  jobId = null,
  contour = 'carta',
}: CartaShellProps): JSX.Element {
  const t = useT()
  const [online, setOnline] = useState(window.navigator.onLine)
  const [outbox, setOutbox] = useState<number | null>(null)
  const [conflicts, setConflicts] = useState<number | null>(null)
  const [commessa, setCommessa] = useState<Commessa | null>(null)
  const [waiting, setWaiting] = useState<number | null>(null)
  const [progress, setProgress] = useState<QualityProgress | null>(null)
  const location = useLocation()
  const params = useParams()
  const inspectionId =
    params.inspectionId !== undefined && params.inspectionId !== ''
      ? params.inspectionId
      : inspectionFromSearch(location.search)
  const paramJob = params.jobId ?? null
  const [resolvedJob, setResolvedJob] = useState<string | null>(jobId ?? paramJob)
  const heading = useRef<HTMLHeadingElement | null>(null)
  const arrival = announce ?? `${location.pathname}${location.search}`
  const inCommessa = inspectionId !== ''
  const inAdmin = !inCommessa && isAdminScreen(location.pathname)
  const inAiuto = !inCommessa && isAiutoScreen(location.pathname)
  // Il nome della schermata nel percorso, quando nessuna scheda e' accesa a
  // dirlo (GI-279): la scheda dell'elemento, la ricevuta di caricamento.
  const screenCrumb = inCommessa && !isTabScreen(location.pathname, inspectionId) ? title : null

  useEffect(() => {
    const onState = (): void => setOnline(window.navigator.onLine)
    window.addEventListener('online', onState)
    window.addEventListener('offline', onState)
    return () => {
      window.removeEventListener('online', onState)
      window.removeEventListener('offline', onState)
    }
  }, [])

  useEffect(() => {
    heading.current?.focus()
  }, [arrival])

  useEffect(() => {
    setResolvedJob(jobId ?? paramJob)
  }, [jobId, paramJob])

  useEffect(() => {
    if (!inCommessa) {
      setOutbox(null)
      setConflicts(null)
      setCommessa(null)
      setProgress(null)
      return
    }
    let cancelled = false
    void countPendingIntents()
      .then((count) => {
        if (!cancelled) setOutbox(count)
      })
      .catch(() => {
        if (!cancelled) setOutbox(null)
      })
    if (who.may.includes(QUALITY_PROGRESS_READ)) {
      void readProgress(inspectionId)
        .then((found) => {
          if (cancelled) return
          setConflicts(found.conflicts)
          setProgress(found)
        })
        .catch(() => {
          if (cancelled) return
          setConflicts(null)
          setProgress(null)
        })
    } else {
      setConflicts(null)
      setProgress(null)
    }
    void commesse()
      .then((rows) => {
        if (cancelled) return
        const found = rows.find((one) => one.inspection_id === inspectionId) ?? null
        setCommessa(found)
        if (jobId === null || jobId === '') {
          setResolvedJob(found?.job_id ?? paramJob)
        }
      })
      .catch(() => {
        if (cancelled) return
        setCommessa(null)
        if (jobId === null || jobId === '') setResolvedJob(paramJob)
      })
    return () => {
      cancelled = true
    }
  }, [inCommessa, inspectionId, jobId, paramJob, who])

  /*
    Il carico della barra si rilegge quando la schermata cambia, non a orologio:
    una pagina che interroga il servizio da sola invecchia male (§ 11) e qui non
    serve, perche' fra un verdetto e l'altro l'operatore cambia schermata
    comunque. Chi non vede il pool non ha carico da vedere.
  */
  useEffect(() => {
    if (!who.may.includes(INSPECTION_POOL_READ)) {
      setWaiting(null)
      return
    }
    let cancelled = false
    void commesse()
      .then((rows) => {
        if (cancelled) return
        // Il lavoro delle commesse congelate non si puo' prendere: contarlo
        // nella pastiglia prometterebbe cio' che il pool poi rifiuta (GI-204).
        setWaiting(
          rows.reduce((total, one) => total + (one.frozen === true ? 0 : one.waiting), 0),
        )
      })
      .catch(() => {
        // Un carico che non si legge non si inventa: meglio nessuna pastiglia
        // che una vecchia.
        if (!cancelled) setWaiting(null)
      })
    return () => {
      cancelled = true
    }
  }, [location.pathname, who])

  return (
    <div data-theme="carta" className="shell shell-carta">
      <a href="#contenuto-principale" className="skip-link">
        {t('chrome.skip')}
      </a>
      <ModuleRail who={who} pathname={location.pathname} waiting={waiting} onLeave={onLeave} />
      <div className="shell-main">
        {/* **Una barra sola, anche in commessa** (ADR-009). Dentro una commessa
            il titolo della schermata e' la scheda accesa: scriverlo una seconda
            volta, in grande, sopra la testa della commessa e sopra le schede,
            costava due fasce di cromo — e sul banco quelle fasce erano
            fotogramma. Il titolo resta nel documento per chi legge con lo
            schermo e per il fuoco all'arrivo, ma non si vede. */}
        <header
          data-theme={contour}
          className={[
            'shell-head',
            inCommessa || inAdmin || inAiuto ? 'shell-head-commessa' : '',
            contour === 'cockpit' ? 'shell-head-scura' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          role="banner"
        >
          <div className={inCommessa || inAdmin || inAiuto ? 'shell-titolo solo-lettori' : 'shell-titolo'}>
            <h1 ref={heading} tabIndex={-1}>
              {title}
            </h1>
            {subtitle !== undefined && <p className="tenue">{subtitle}</p>}
          </div>
          {inCommessa && commessa !== null && (
            <WorkspaceHeader
              inspectionId={inspectionId}
              commessa={commessa}
              progress={progress}
              who={who}
              screen={screenCrumb}
            />
          )}
          {inCommessa && <WorkspaceTabs inspectionId={inspectionId} who={who} jobId={resolvedJob} />}
          {/* L'amministrazione e' un modulo con le sue schede (GI-279): cinque
              pagine senza un collegamento fra loro erano quattro pagine che
              non esistevano per chi non ne conosceva l'indirizzo. */}
          {inAdmin && <AdminTabs who={who} />}
          {/* L'aiuto e' un modulo con le sue schede (GI-281): manuale, glossario, tasti. */}
          {inAiuto && <AiutoTabs who={who} />}
          {/* Chi sei sta nella barra, in fondo, nel disco con le iniziali
              (ADR-009): la testa tiene solo lo stato — e solo quando c'e'
              qualcosa da dire. */}
          <div className="shell-stato">
            {/* **La norma tace, lo scarto parla** (ADR-009). «In linea»,
                «casella d'uscita 0» e «conflitti 0» erano tre pastiglie
                sempre accese per dire che non c'era niente da dire. Restano
                nel documento per chi legge con lo schermo — la regione viva
                deve esistere per poter annunciare il cambio — ma si vedono
                solo quando c'e' un guasto o un numero. */}
            <span role="status">
              {online ? (
                <span className="solo-lettori">{t('chrome.online')}</span>
              ) : (
                <StatusPill label={t('chrome.offline')} tone="sev-4" />
              )}
            </span>
            {inCommessa && outbox !== null && outbox > 0 && (
              <>
                <StatusPill label={t('chrome.outbox', { n: outbox })} tone="sev-2" />
                {/* La spiegazione sta accanto al numero che allarma: chi vede
                    «casella d'uscita 7» si chiede se ha perso sette verdetti,
                    ed e' il momento in cui la risposta serve. */}
                <Hint about="outbox" />
              </>
            )}
            {inCommessa && conflicts !== null && conflicts > 0 && (
              <StatusPill label={t('chrome.conflicts', { n: conflicts })} tone="sev-3" />
            )}
          </div>
        </header>
        <div id="contenuto-principale">
          {/* **Solo sulla schermata d'arrivo.** Il riquadro dice chi sei e che
              cosa fai: ripetuto su ogni schermata sarebbe rumore, e dopo tre
              volte non lo leggerebbe piu' nessuno — nemmeno il primo giorno,
              che e' l'unico in cui serve davvero. La schermata d'arrivo la sa
              gia' `defaultPathFor`, la stessa che porta qui dopo l'accesso:
              chiederla a lei invece di elencarla di nuovo evita che le due
              risposte si separino il giorno in cui una cambia. */}
          {location.pathname === defaultPathFor(who) && <StartPanel who={who} />}
          {/* Che cosa si fa qui, una riga (GI-281): non sulla schermata
              d'arrivo, dove il riquadro lo dice gia'. Rimontata a ogni
              indirizzo, cosi' «prima volta» e' della schermata e non del giro. */}
          {location.pathname !== defaultPathFor(who) && (
            // Col contorno del modulo: sul banco scuro una riga chiara era una
            // fascia bianca sopra il nero.
            <div data-theme={contour} className="scopo-contorno">
              <ScreenPurpose key={location.pathname} pathname={location.pathname} login={who.login} />
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  )
}
